import moment, { type Moment } from 'moment';

import type {
  AbsolutePeriodValue,
  PeriodDateType,
  RelativeAbsolutePeriodValue,
  RelativePeriodValue,
} from './RelativeAbsolutePeriodFilter.types';

export const DEFAULT_RELATIVE_PERIOD: RelativePeriodValue = {
  isRelative: true,
  relativeAmount: 1,
  dateType: 'day',
};

export const isRelativePeriodValue = (
  value: unknown,
): value is RelativePeriodValue =>
  value != null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  ((value as RelativePeriodValue).isRelative === true ||
    (value as { isRelative?: unknown }).isRelative === 'true');

export const createDefaultAbsoluteRange = (): [Moment, Moment] => [
  moment().subtract(7, 'days').startOf('day'),
  moment().endOf('day'),
];

export const toAbsolutePeriodValue = (
  dateRange: [Moment, Moment] | null = createDefaultAbsoluteRange(),
  dateType: PeriodDateType = 'day',
): AbsolutePeriodValue => ({
  isRelative: false,
  dateRange,
  dateType,
});

/**
 * Resolves a relative/absolute period into a millis window for queries.
 * Relative windows are calendar-aligned: "N units before, until today"
 * means from startOf(day) of (now - N units) through endOf(today).
 */
export const resolvePeriodToMillis = (
  value: RelativeAbsolutePeriodValue | null | undefined,
): [number, number] | null => {
  if (value == null) {
    return null;
  }

  if (isRelativePeriodValue(value)) {
    const amount = value.relativeAmount > 0 ? value.relativeAmount : 1;
    const startMs = moment().subtract(amount, value.dateType).startOf('day').valueOf();
    const endMs = moment().endOf('day').valueOf();
    return [startMs, endMs];
  }

  const range = value.dateRange;
  if (!Array.isArray(range) || range.length !== 2 || !range[0] || !range[1]) {
    return null;
  }

  return [moment(range[0]).valueOf(), moment(range[1]).valueOf()];
};
